import { useState, useMemo } from 'react';
import { Search, Users, Swords, Crown, AlertTriangle, HelpCircle, X } from 'lucide-react';
import { GameHistoryItem } from '../modules/stats/types';
import { DojosRow, SeasonsRow } from '../types/database';
import TimeFilter from './TimeFilter';

interface OpponentAnalysisProps {
  games: GameHistoryItem[];
  currentPlayerNumber: number;
  currentTargetPlayerName?: string;
  _allPlayers?: { player_number: number; player_nickname: string }[];
  // V146: 新增筛选相关属性
  seasons?: SeasonsRow[];
  dojos?: DojosRow[];
}

interface PlayerRelation {
  playerName: string;
  teammateGames: number;
  teammateWins: number;
  teammateLosses: number;
  teammateWinRate: number;
  opponentGames: number;
  opponentWins: number;
  opponentLosses: number;
  opponentWinRate: number;
}

// V138: 统计逻辑说明数据
// V146: 更新文案"最废累赘"→"狗鸡毛·臭鱼烂虾"
// V201: 更新排序逻辑说明文案
const STATS_EXPLANATIONS = {
  bestPartner: {
    title: '最佳搭档统计逻辑',
    formula: '同队场次 ≥ 5 场',
    description: '统计与你同队胜率最高的玩家',
    details: [
      '条件 1：与你同队（同属正义或邪恶阵营）',
      '条件 2：该场比赛你们队伍获胜',
      '条件 3：同队场次 ≥ 5 场才显示',
      '',
      '排序规则：',
      '1. 优先按胜率降序排列',
      '2. 胜率相同时，按同队场次降序排列',
    ],
    example: '例如：你和玩家 A 同队 10 场，其中 7 场获胜→ 最佳搭档排名第 1',
  },
  worstOpponent: {
    title: '一生之敌统计逻辑',
    formula: '对阵场次 ≥ 5 场',
    description: '统计与你对阵时让你失败率最高的玩家',
    details: [
      '条件 1：与你不同队（分属不同阵营）',
      '条件 2：该场比赛对方阵营获胜',
      '条件 3：对阵场次 ≥ 5 场才显示',
      '',
      '排序规则：',
      '1. 优先按失败率降序排列',
      '2. 失败率相同时，按对阵场次降序排列',
    ],
    example: '例如：玩家 B 与你对阵 8 场，其中 5 场击败你→ 一生之敌排名第 1',
  },
  worstTeammate: {
    title: '狗鸡毛·臭鱼烂虾统计逻辑',
    formula: '同队场次 ≥ 5 场',
    description: '统计与你同队失败率最高的玩家',
    details: [
      '条件 1：与你同队（同属正义或邪恶阵营）',
      '条件 2：该场比赛你们队伍失败',
      '条件 3：同队场次 ≥ 5 场才显示',
      '',
      '排序规则：',
      '1. 优先按失败率降序排列',
      '2. 失败率相同时，按同队场次降序排列',
    ],
    example: '例如：你和玩家 C 同队 6 场，其中 4 场失败→ 狗鸡毛·臭鱼烂虾排名第 1',
  },
};

export default function OpponentAnalysis({
  games,
  currentPlayerNumber,
  currentTargetPlayerName,
  // V146: 新增筛选属性
  seasons = [],
  dojos = [],
}: OpponentAnalysisProps) {
  const [searchQuery, setSearchQuery] = useState('');
  // V138: 统计逻辑说明弹窗状态
  const [showExplanation, setShowExplanation] = useState<string | null>(null);
  
  // V149: 使用 TimeFilter 组件的状态
  const [filteredGames, setFilteredGames] = useState<GameHistoryItem[]>(games);
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  const [selectedDojo, setSelectedDojo] = useState<string>('all');

  // V149: 初始化时同步 games
  useMemo(() => {
    setFilteredGames(games);
  }, [games]);

  // V149: 处理筛选变化 - 使用 TimeFilter 组件
  const handleFilterChange = (startDate: Date, endDate: Date) => {
    let filtered = [...games];
    
    // 时间范围筛选
    filtered = filtered.filter(g => {
      if (!g.game_date) return false;
      const gameDate = new Date(g.game_date);
      return gameDate >= startDate && gameDate <= endDate;
    });
    
    // 应用赛季筛选
    if (selectedSeason !== 'all') {
      filtered = filtered.filter(g => g.season_id === parseInt(selectedSeason));
    }
    
    // 应用道场筛选
    if (selectedDojo !== 'all') {
      filtered = filtered.filter(g => g.dojo_id === parseInt(selectedDojo));
    }
    
    setFilteredGames(filtered);
    console.log('[V149] 对阵分析时间筛选:', { startDate, endDate, filteredCount: filtered.length });
  };
  
  // V149: 处理赛季变化
  const handleSeasonChange = (seasonId: string) => {
    setSelectedSeason(seasonId);
    
    let filtered = [...games];
    
    // 应用赛季筛选
    if (seasonId !== 'all') {
      filtered = filtered.filter(g => g.season_id === parseInt(seasonId));
    }
    
    // 应用道场筛选
    if (selectedDojo !== 'all') {
      filtered = filtered.filter(g => g.dojo_id === parseInt(selectedDojo));
    }
    
    setFilteredGames(filtered);
    console.log('[V149] 对阵分析赛季筛选:', { seasonId, filteredCount: filtered.length });
  };
  
  // V149: 处理道场变化
  const handleDojoChange = (dojoId: string) => {
    setSelectedDojo(dojoId);
    
    let filtered = [...games];
    
    // 应用道场筛选
    if (dojoId !== 'all') {
      filtered = filtered.filter(g => g.dojo_id === parseInt(dojoId));
    }
    
    // 应用赛季筛选
    if (selectedSeason !== 'all') {
      filtered = filtered.filter(g => g.season_id === parseInt(selectedSeason));
    }
    
    setFilteredGames(filtered);
    console.log('[V149] 对阵分析道场筛选:', { dojoId, filteredCount: filtered.length });
  };

  // V149: 重构对阵分析统计逻辑 - 按昵称关联，使用 filteredGames
  const relations = useMemo<PlayerRelation[]>(() => {
    const relationMap = new Map<string, PlayerRelation>();

    // V138: 确定当前目标玩家昵称（优先使用传入的，否则从游戏中查找）
    const targetPlayerName = currentTargetPlayerName || 
      filteredGames.flatMap(g => g.players || [])
        .find(p => p.player_number === currentPlayerNumber)?.player_nickname;

    if (!targetPlayerName) {
      console.log('[V138] 未找到目标玩家昵称:', { currentPlayerNumber });
      return [];
    }

    console.log('[V146] 对阵分析 - 目标玩家:', targetPlayerName, '筛选后游戏数:', filteredGames.length);

    filteredGames.forEach(game => {
      // V138: 使用昵称匹配当前玩家
      const currentPlayer = game.players?.find(p => 
        p.player_nickname === targetPlayerName
      );
      if (!currentPlayer) return;

      // V138: 判断当前玩家是否获胜
      const isCurrentPlayerWin = currentPlayer.faction === game.winning_faction;

      game.players?.forEach(player => {
        // V138: 跳过当前玩家自己
        if (player.player_nickname === targetPlayerName) return;

        const otherPlayerName = player.player_nickname;
        if (!otherPlayerName) return;

        // V138: 判断是否为同队
        const isTeammate = player.faction === currentPlayer.faction;
        // V138: 判断其他玩家是否获胜
        const isOtherPlayerWin = player.faction === game.winning_faction;

        // V138: 初始化关系记录
        if (!relationMap.has(otherPlayerName)) {
          relationMap.set(otherPlayerName, {
            playerName: otherPlayerName,
            teammateGames: 0,
            teammateWins: 0,
            teammateLosses: 0,
            teammateWinRate: 0,
            opponentGames: 0,
            opponentWins: 0,
            opponentLosses: 0,
            opponentWinRate: 0,
          });
        }

        const relation = relationMap.get(otherPlayerName)!;

        if (isTeammate) {
          // V138: 同队统计
          relation.teammateGames++;
          if (isCurrentPlayerWin && isOtherPlayerWin) {
            // V138: 最佳搭档条件：当前玩家获胜 + 同队 + 其他玩家也获胜
            relation.teammateWins++;
          } else if (!isCurrentPlayerWin && !isOtherPlayerWin) {
            // V138: 最废累赘条件：当前玩家失败 + 同队 + 其他玩家也失败
            relation.teammateLosses++;
          }
        } else {
          // V138: 对阵统计
          relation.opponentGames++;
          if (!isCurrentPlayerWin && isOtherPlayerWin) {
            // V138: 一生之敌条件：当前玩家失败 + 不同队 + 其他玩家获胜
            relation.opponentWins++;
          } else if (isCurrentPlayerWin && !isOtherPlayerWin) {
            // V138: 对阵胜利条件：当前玩家获胜 + 不同队 + 其他玩家失败
            relation.opponentLosses++;
          }
        }
      });
    });

    // V138: 计算胜率并转换为数组
    const result = Array.from(relationMap.values()).map(r => ({
      ...r,
      teammateWinRate: r.teammateGames > 0 ? Math.round((r.teammateWins / r.teammateGames) * 100) : 0,
      opponentWinRate: r.opponentGames > 0 ? Math.round((r.opponentWins / r.opponentGames) * 100) : 0,
    }));

    console.log('[V138] 对阵分析 - 关系统计结果:', {
      targetPlayer: targetPlayerName,
      totalRelations: result.length,
      sample: result.slice(0, 3),
    });

    return result;
  }, [filteredGames, currentPlayerNumber, currentTargetPlayerName]);

  // V138: 最佳搭档 Top5 - 当前玩家获胜+同队+其他玩家也获胜，按次数降序
  // V138: 最佳搭档 Top5 - 按同队获胜率排序
  // V195: 改为按胜率排名，筛选条件改为同队场次>=3
  const bestPartners = useMemo(() => {
    return relations
      .filter(r => r.teammateGames >= 5) // V195: 同队场次至少 5 场
      .sort((a, b) => {
        // 按同队获胜率降序
        if (b.teammateWinRate !== a.teammateWinRate) {
          return b.teammateWinRate - a.teammateWinRate;
        }
        // 胜率相同按同队场次降序
        return b.teammateGames - a.teammateGames;
      })
      .slice(0, 5);
  }, [relations]);

  // V138: 一生之敌 Top5 - 当前玩家失败+不同队+其他玩家获胜，按次数降序
  // V138: 一生之敌 Top5 - 按敌对失败率排序
  // V195: 改为按敌对失败率排名，筛选条件改为对阵场次>=3
  const worstOpponents = useMemo(() => {
    return relations
      .filter(r => r.opponentGames >= 5) // V195: 对阵场次至少 5 场
      .sort((a, b) => {
        // 按敌对失败率降序（即 opponentWinRate）
        if (b.opponentWinRate !== a.opponentWinRate) {
          return b.opponentWinRate - a.opponentWinRate;
        }
        // 失败率相同按对阵场次降序
        return b.opponentGames - a.opponentGames;
      })
      .slice(0, 5);
  }, [relations]);

  // V138: 狗鸡毛·臭鱼烂虾 Top5 - 当前玩家失败+同队+其他玩家也失败，按次数降序
  // V146: 更新文案 "最废累赘" → "狗鸡毛·臭鱼烂虾"
  // V138: 狗鸡毛·臭鱼烂虾 Top5 - 按同队失败率排序
  // V146: 更新文案 "最废累赘" → "狗鸡毛·臭鱼烂虾"
  // V195: 改为按同队失败率排名，筛选条件改为同队场次>=3
  const worstTeammates = useMemo(() => {
    return relations
      .filter(r => r.teammateGames >= 5) // V195: 同队场次至少 5 场
      .sort((a, b) => {
        // 按同队失败率降序
        const aFailRate = a.teammateGames > 0 ? (a.teammateLosses / a.teammateGames) : 0;
        const bFailRate = b.teammateGames > 0 ? (b.teammateLosses / b.teammateGames) : 0;
        if (bFailRate !== aFailRate) {
          return bFailRate - aFailRate;
        }
        // 失败率相同按同队场次降序
        return b.teammateGames - a.teammateGames;
      })
      .slice(0, 5);
  }, [relations]);

  const searchedPlayer = useMemo(() => {
    if (!searchQuery.trim()) return null;
    return relations.find(r =>
      r.playerName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, relations]);

  // V138: 渲染统计逻辑说明弹窗
  const renderExplanationModal = () => {
    if (!showExplanation) return null;
    
    const explanation = STATS_EXPLANATIONS[showExplanation as keyof typeof STATS_EXPLANATIONS];
    if (!explanation) return null;

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-avalon-card rounded-xl border border-avalon-purple/50 max-w-md w-full p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-avalon-gold" />
              {explanation.title}
            </h3>
            <button
              onClick={() => setShowExplanation(null)}
              className="text-avalon-text hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="p-3 bg-avalon-bg-dark/50 rounded-lg">
              <div className="text-avalon-text text-xs mb-1">筛选条件</div>
              <div className="text-avalon-gold font-medium">{explanation.formula}</div>
            </div>
            
            <div>
              <div className="text-avalon-text text-sm mb-2">{explanation.description}</div>
              <ul className="space-y-1.5">
                {explanation.details.map((detail, index) => (
                  <li key={index} className="text-white text-sm flex items-start gap-2">
                    <span className="text-avalon-gold mt-1">•</span>
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="p-3 bg-avalon-purple/20 rounded-lg border border-avalon-purple/30">
              <div className="text-avalon-text text-xs mb-1">示例</div>
              <div className="text-white text-sm">{explanation.example}</div>
            </div>
          </div>
          
          <button
            onClick={() => setShowExplanation(null)}
            className="w-full mt-5 py-2.5 bg-avalon-purple text-white rounded-lg hover:bg-avalon-purple/80 transition-colors"
          >
            明白了
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* V138: 统计逻辑说明弹窗 */}
      {renderExplanationModal()}
      
      {/* V147: 筛选器区域 - 统一为两行布局，与 TimeFilter 样式一致 */}
      {/* V149: 使用 TimeFilter 组件替代独立筛选器 */}
      <div className="mb-4">
        <TimeFilter 
          onFilterChange={handleFilterChange}
          seasons={seasons.map(s => ({ id: String(s.id), name: s.name }))}
          venues={dojos.map(d => ({ id: String(d.id), name: d.name }))}
          onSeasonChange={handleSeasonChange}
          onVenueChange={handleDojoChange}
        />
      </div>
      
      {/* 搜索框 */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-avalon-text" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="搜索玩家昵称..."
          className="w-full pl-10 pr-4 py-2 bg-avalon-card border border-avalon-purple/30 rounded-xl text-white placeholder-avalon-text focus:outline-none focus:border-avalon-purple"
        />
      </div>

      {/* 搜索结果 */}
      {searchedPlayer && (
        <div className="p-4 bg-avalon-card rounded-xl border border-avalon-purple/30">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-avalon-gold" />
            与 {searchedPlayer.playerName} 的对阵数据
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-avalon-bg-dark/50 rounded-lg">
              <div className="text-avalon-text text-xs mb-1">同队场次</div>
              <div className="text-white font-bold">{searchedPlayer.teammateGames} 场</div>
              <div className="text-green-400 text-xs">{searchedPlayer.teammateWins} 共同胜 / {searchedPlayer.teammateLosses} 共同负</div>
              <div className="text-avalon-gold text-sm font-medium">{searchedPlayer.teammateWinRate}% 胜率</div>
            </div>
            <div className="p-3 bg-avalon-bg-dark/50 rounded-lg">
              <div className="text-avalon-text text-xs mb-1">对阵场次</div>
              <div className="text-white font-bold">{searchedPlayer.opponentGames} 场</div>
              <div className="text-green-400 text-xs">{searchedPlayer.opponentLosses} 胜 / {searchedPlayer.opponentWins} 负</div>
              <div className="text-avalon-gold text-sm font-medium">{100 - searchedPlayer.opponentWinRate}% 胜率</div>
            </div>
          </div>
        </div>
      )}

      {/* 排行榜 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* 最佳搭档 */}
        <div className="p-4 bg-avalon-card rounded-xl border border-avalon-purple/30">
          {/* V138: 标题栏添加说明按钮 */}
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white text-sm font-medium flex items-center gap-2">
              <Crown className="w-4 h-4 text-yellow-400" />
              最佳搭档 Top 5
            </h4>
            <button
              onClick={() => setShowExplanation('bestPartner')}
              className="w-6 h-6 rounded-full bg-avalon-purple/50 flex items-center justify-center text-avalon-text hover:text-white hover:bg-avalon-purple transition-colors"
              title="查看统计逻辑"
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          </div>
          {bestPartners.length > 0 ? (
            <div className="space-y-2">
              {bestPartners.map((r, i) => (
                <div key={r.playerName} className="flex items-center justify-between p-2 bg-avalon-bg-dark/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs text-white ${
                      i === 0 ? 'bg-yellow-500' :
                      i === 1 ? 'bg-gray-400' :
                      i === 2 ? 'bg-orange-500' :
                      'bg-avalon-purple/50'
                    }`}>
                      {i + 1}
                    </span>
                    <span className="text-white text-sm truncate max-w-[80px]">{r.playerName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-yellow-400 text-xs font-medium">胜率{r.teammateWinRate}%</span>
                    <span className="text-green-400 text-sm font-medium">{r.teammateWins}胜<span className="text-avalon-text text-xs ml-1">({r.teammateGames}场)</span></span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-avalon-text text-sm text-center py-4">
              暂无数据（需共同获胜≥3场）
            </div>
          )}
        </div>

        {/* 一生之敌 */}
        <div className="p-4 bg-avalon-card rounded-xl border border-avalon-purple/30">
          {/* V138: 标题栏添加说明按钮 */}
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white text-sm font-medium flex items-center gap-2">
              <Swords className="w-4 h-4 text-red-400" />
              一生之敌 Top 5
            </h4>
            <button
              onClick={() => setShowExplanation('worstOpponent')}
              className="w-6 h-6 rounded-full bg-avalon-purple/50 flex items-center justify-center text-avalon-text hover:text-white hover:bg-avalon-purple transition-colors"
              title="查看统计逻辑"
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          </div>
          {worstOpponents.length > 0 ? (
            <div className="space-y-2">
              {worstOpponents.map((r, i) => (
                <div key={r.playerName} className="flex items-center justify-between p-2 bg-avalon-bg-dark/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs text-white ${
                      i === 0 ? 'bg-red-500' :
                      i === 1 ? 'bg-red-400' :
                      i === 2 ? 'bg-red-300' :
                      'bg-red-500/50'
                    }`}>
                      {i + 1}
                    </span>
                    <span className="text-white text-sm truncate max-w-[80px]">{r.playerName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-purple-400 text-xs font-medium">失败率{r.opponentWinRate}%</span>
                    <span className="text-red-400 text-sm font-medium">{r.opponentWins}胜<span className="text-avalon-text text-xs ml-1">({r.opponentGames}场)</span></span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-avalon-text text-sm text-center py-4">
              暂无数据（需战胜你≥3场）
            </div>
          )}
        </div>

        {/* V146: 狗鸡毛·臭鱼烂虾 */}
        <div className="p-4 bg-avalon-card rounded-xl border border-avalon-purple/30">
          {/* V138: 标题栏添加说明按钮 */}
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-400" />
              <span className="text-sm">狗鸡毛·臭鱼烂虾 Top 5</span>
            </h4>
            <button
              onClick={() => setShowExplanation('worstTeammate')}
              className="w-6 h-6 rounded-full bg-avalon-purple/50 flex items-center justify-center text-avalon-text hover:text-white hover:bg-avalon-purple transition-colors"
              title="查看统计逻辑"
            >
              <HelpCircle className="w-3.5 h-3.5" />
            </button>
          </div>
          {worstTeammates.length > 0 ? (
            <div className="space-y-2">
              {worstTeammates.map((r, i) => (
                <div key={r.playerName} className="flex items-center justify-between p-2 bg-avalon-bg-dark/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs text-white ${
                      i === 0 ? 'bg-orange-500' :
                      i === 1 ? 'bg-orange-400' :
                      i === 2 ? 'bg-orange-300' :
                      'bg-orange-500/50'
                    }`}>
                      {i + 1}
                    </span>
                    <span className="text-white text-sm truncate max-w-[80px]">{r.playerName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-blue-400 text-xs font-medium">失败率{r.teammateGames > 0 ? Math.round((r.teammateLosses / r.teammateGames) * 100) : 0}%</span>
                    <span className="text-orange-400 text-sm font-medium">{r.teammateLosses}负<span className="text-avalon-text text-xs ml-1">({r.teammateGames}场)</span></span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-avalon-text text-sm text-center py-4">
              暂无数据（需共同失败≥3场）
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
